import { ref, computed } from 'vue'
import { bridge, BRIDGE_METHODS } from '@h5-journey/bridge'

interface SystemBarData {
  statusBarHeight: number
  bottomSafeAreaHeight?: number
}

const CACHE_KEY = 'systemStatusBarHeight'

const getCachedHeight = (): number => {
  const cached = sessionStorage.getItem(CACHE_KEY)
  return cached ? parseInt(cached) : 0
}

const setCachedHeight = (height: number): void => {
  sessionStorage.setItem(CACHE_KEY, height.toString())
}

// 全局共享的状态栏高度响应式引用
const statusBarHeight = ref(getCachedHeight())

/**
 * 获取系统状态栏高度
 * @returns 状态栏高度（px）
 */
export const getSystemBarHeight = async (): Promise<number> => {
  if (statusBarHeight.value > 0) {
    return statusBarHeight.value
  }

  try {
    const res = await bridge.invokeSync<SystemBarData>(BRIDGE_METHODS.GET_SYSTEM_BAR_HEIGHT)
    const height = res?.result?.data?.statusBarHeight || 0

    statusBarHeight.value = height
    setCachedHeight(height)

    return height
  } catch (error) {
    console.error('获取状态栏高度失败:', error)
    return statusBarHeight.value
  }
}

/**
 * 清除状态栏高度缓存
 */
export const clearStatusBarCache = (): void => {
  sessionStorage.removeItem(CACHE_KEY)
  statusBarHeight.value = 0
}

/**
 * 获取状态栏高度的响应式引用
 * 首次调用时若缓存为空会自动通过 bridge 获取
 */
export const useStatusBarHeight = () => {
  if (statusBarHeight.value === 0) {
    getSystemBarHeight()
  }

  return {
    statusBarHeight,
    refresh: getSystemBarHeight,
    clear: clearStatusBarCache
  }
}

/**
 * 计算导航栏总高度（48px + 状态栏高度）
 */
export const getNavBarTotalHeight = async (): Promise<number> => {
  const barHeight = await getSystemBarHeight()
  return 48 + barHeight
}

/**
 * 获取导航栏总高度的响应式计算属性
 */
export const useNavBarTotalHeight = () => {
  const { statusBarHeight, refresh, clear } = useStatusBarHeight()

  const totalHeight = computed(() => 48 + statusBarHeight.value)

  return {
    totalHeight,
    statusBarHeight,
    refresh,
    clear
  }
}

/**
 * 获取底部安全区域高度
 */
export const getBottomSafeAreaHeight = async (): Promise<number> => {
  try {
    const res = await bridge.invokeSync<SystemBarData>(BRIDGE_METHODS.GET_SYSTEM_BAR_HEIGHT)
    return res?.result?.data?.bottomSafeAreaHeight || 0
  } catch {
    return 0
  }
}

/**
 * 计算吸顶组件的精确偏移量
 * iOS Safari 存在 2-3px 的吸顶偏移问题，需要修正
 */
export const calculateStickyOffset = (baseHeight: number): number => {
  const isIOS = /iphone|ipad|ipod|ios/i.test(navigator.userAgent)
  const correction = isIOS ? 3 : 2
  return Math.max(baseHeight - correction, 0)
}
